export type PlatformTabId =
  | "about"
  | "release-history"
  | "public-roadmap"
  | "public-manual"
  | "glossary"
  | "spatial";

export interface PlatformTable {
  caption: string;
  headers: string[];
  rows: string[][];
  rowHeaderColumn?: number;
  columnWidths?: string[];
}

export interface PlatformFormField {
  id: string;
  label: string;
  hint: string;
  placeholder?: string;
  inputType?: "text" | "email" | "url" | "number" | "date";
}

export interface PlatformForm {
  caption: string;
  submitLabel?: string;
  fields: PlatformFormField[];
}

export type PlatformBlock =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[]; ordered?: boolean }
  | { type: "table"; table: PlatformTable }
  | { type: "form"; form: PlatformForm }
  | { type: "callout"; title: string; body: string };

export interface PlatformSection {
  heading: string;
  blocks: PlatformBlock[];
}

export interface PlatformTab {
  id: PlatformTabId;
  label: string;
  slug: string;
  title: string;
  summary: string;
  sections: PlatformSection[];
}

export const platformTabs: PlatformTab[] = [
  {
    id: "about",
    label: "About Artetra",
    slug: "about",
    title: "Artetra Platform",
    summary:
      "A cultural heritage publishing environment for preparing, validating, and sharing digital collections in browser-based 3D spaces.",
    sections: [
      {
        heading: "Public Positioning",
        blocks: [
          {
            type: "paragraph",
            text:
              "Artetra is designed for museums, galleries, archives, educators, cultural institutions, and reuse-focused users who need accessible ways to present artworks, 3D objects, and contextual metadata online.",
          },
          {
            type: "paragraph",
            text:
              "The platform combines public gallery viewing, 3D asset preview, VR-ready inspection, curated artwork records, metadata validation, reusable publication surfaces, offline gallery viewing, and privacy-conscious observability.",
          },
          {
            type: "list",
            items: [
              "Present 3D cultural heritage objects in accessible browser-based spaces.",
              "Prepare collection material for public viewing and reuse.",
              "Improve metadata quality before publication.",
              "Preserve rights, source links, and contextual information during reuse workflows.",
              "Share galleries with audiences, reviewers, partners, and educational users.",
            ],
          },
        ],
      },
      {
        heading: "Public Maturity Tracks",
        blocks: [
          {
            type: "table",
            table: {
              caption: "Current Artetra maturity tracks",
              headers: ["Track", "Description", "Public value"],
              rowHeaderColumn: 0,
              rows: [
                [
                  "Content experience",
                  "Public galleries, 3D viewing, VR-ready inspection, mobile and desktop viewing, and offline gallery replay for prepared public galleries.",
                  "Visitors and reviewers can experience cultural heritage objects in richer digital spaces.",
                ],
                [
                  "Publishing workflow",
                  "Structured artwork records, metadata review, rights and source information, validation feedback, landing pages, embeds, and Europeana-compatible metadata direction.",
                  "Curators can prepare records more consistently before publication and reuse.",
                ],
                [
                  "Operational maturity",
                  "Browser delivery, asset optimization direction, public-gallery resilience, privacy-conscious analytics, issue-based planning, CI/CD checks, and clearer publication states.",
                  "Institutions and partners can understand readiness, quality, and delivery status more clearly.",
                ],
              ],
            },
          },
        ],
      },
      {
        heading: "Product Management And Delivery Maturity",
        blocks: [
          {
            type: "paragraph",
            text:
              "Artetra maturity is not only feature growth. It also covers how work is planned, verified, and operated through issue tracking, repository history, CI/CD checks, infrastructure readiness, role boundaries, and verification records.",
          },
          {
            type: "table",
            table: {
              caption: "Public delivery maturity areas",
              headers: ["Area", "Direction", "Public value"],
              rowHeaderColumn: 0,
              rows: [
                [
                  "Planning",
                  "Roadmap work is connected to tickets, release records, and verification artifacts.",
                  "Platform evolution is easier to audit and explain.",
                ],
                [
                  "Engineering",
                  "Git history, focused tests, builds, and CI/CD checks are treated as release-readiness evidence.",
                  "Changes can be reviewed against concrete quality signals.",
                ],
                [
                  "Operations",
                  "Infrastructure-as-code direction, health checks, telemetry, and deployment boundaries reduce hidden manual drift.",
                  "Public-gallery reliability can mature without exposing private systems.",
                ],
                [
                  "Roles",
                  "Public visitors, curators, administrators, strict administrators, and operators have distinct responsibilities.",
                  "Public surfaces remain separate from editorial and operational controls.",
                ],
              ],
            },
          },
        ],
      },
    ],
  },
  {
    id: "release-history",
    label: "Release History",
    slug: "release-history",
    title: "Release History",
    summary:
      "Public milestone windows for the Artetra platform. Dates are broad product periods, not a full engineering changelog.",
    sections: [
      {
        heading: "Release Summary",
        blocks: [
          {
            type: "table",
            table: {
              caption: "Artetra public release summary",
              headers: ["Version", "Period", "Maturity label", "Public use case"],
              rowHeaderColumn: 0,
              columnWidths: ["96px", "150px", "260px", "auto"],
              rows: [
                [
                  "v1",
                  "2023-2025",
                  "Virtual gallery and exhibitor foundation",
                  "Cultural and creative teams building virtual galleries, managing artworks, and presenting digital exhibitions.",
                ],
                [
                  "v2",
                  "Oct 2025-Feb 2026",
                  "External-source reuse and curator workflow maturity",
                  "Curators and reuse users importing public collection material and previewing it in Artetra gallery contexts.",
                ],
                [
                  "v3",
                  "Mar-Jun 2026",
                  "Europeana-compatible 3D/VR publishing and platform maturity",
                  "Cultural heritage institutions preparing 3D records for public reuse, VR inspection, offline viewing, and aggregator-compatible publication workflows.",
                ],
              ],
            },
          },
        ],
      },
      {
        heading: "v1: Virtual Gallery And Exhibitor Foundation",
        blocks: [
          {
            type: "paragraph",
            text:
              "The first Artetra release period established browser-based 3D spaces, gallery management, artwork creation and listing, metadata entry, user workflows, artwork positioning, mobile controls, and early VR support.",
          },
          {
            type: "list",
            items: [
              "Cultural and creative teams could create digital exhibition spaces.",
              "Artworks could be added, described, positioned, and reviewed in gallery contexts.",
              "The platform foundation was established for later 3D, metadata, optimization, and publication improvements.",
            ],
          },
        ],
      },
      {
        heading: "v2: External-Source Reuse And Curator Workflow Maturity",
        blocks: [
          {
            type: "paragraph",
            text:
              "The second release period expanded Artetra toward reuse-oriented cultural heritage workflows with external public-source import and search direction, collection browsing, rights and source metadata preservation, gallery preview from collection identifiers, safer external media handling, and improved render-setting workflows.",
          },
          {
            type: "list",
            items: [
              "Curators could work more directly with public collection material.",
              "Source information, rights information, and reuse context became central to the workflow.",
              "Collection material could be previewed in Artetra spaces before broader publication or reuse.",
              "Browser-safe 3D delivery and media validation became clearer product requirements.",
            ],
          },
        ],
      },
      {
        heading: "v3: Europeana-Compatible 3D/VR Publishing And Platform Maturity",
        blocks: [
          {
            type: "paragraph",
            text:
              "The third release period focused on publication readiness, 3D/VR maturity, public reuse surfaces, and operational quality. Public-facing capabilities include EDM/RDF/OAI publication direction, oEmbed-style reuse surfaces, public artwork landing and embed delivery, publication states, readiness and validation feedback, technical metadata direction, unified artwork preparation, offline gallery viewing, privacy-conscious analytics, and stronger VR/gallery runtime behavior.",
          },
          {
            type: "table",
            table: {
              caption: "v3 maturity areas",
              headers: ["Area", "Public capability"],
              rowHeaderColumn: 0,
              rows: [
                [
                  "EDM and validation",
                  "EDM-style publication surfaces, tier-aware readiness feedback, language and identifier checks, rights clarity, and contextual metadata direction.",
                ],
                [
                  "3D and VR",
                  "3D asset review, viewer framing, controller/raycast hardening, VR metadata panels, and safer interaction fallbacks.",
                ],
                [
                  "Offline gallery viewing",
                  "Prepared public-gallery assets can be replayed when connectivity is limited.",
                ],
                [
                  "Observability",
                  "Privacy-conscious telemetry, admin analytics, request correlation, and runtime-quality signals.",
                ],
                [
                  "Infrastructure and workflow",
                  "Infrastructure-as-code direction, optimization workflow evidence, CI/CD discipline, and issue-based product management.",
                ],
              ],
            },
          },
        ],
      },
    ],
  },
  {
    id: "public-roadmap",
    label: "Public Roadmap",
    slug: "public-roadmap",
    title: "Public Roadmap",
    summary:
      "A 12-24 month direction for cultural heritage publishing, 3D delivery, metadata quality, reuse, and platform maturity.",
    sections: [
      {
        heading: "Estimated Roadmap Overview",
        blocks: [
          {
            type: "table",
            table: {
              caption: "Artetra public roadmap",
              headers: ["Area", "Public direction", "Estimated window", "Public outcome"],
              rowHeaderColumn: 0,
              columnWidths: ["220px", "38%", "160px", "auto"],
              rows: [
                [
                  "EDM publication surfaces",
                  "Harden EDM-style export profiles, OAI-style harvesting behavior, landing pages, and validation feedback.",
                  "H2 2026",
                  "More predictable aggregator-ready publication packages.",
                ],
                [
                  "Metadata validation",
                  "Make readiness feedback clearer for required fields, language values, identifiers, rights, source attribution, and contextual metadata.",
                  "H2 2026",
                  "Curators can see what blocks publication and what improves reuse quality.",
                ],
                [
                  "Controlled vocabularies",
                  "Expand use of controlled vocabularies, authority files, thesauri, SKOS concept schemes, and dereferenceable URI references.",
                  "H2 2026-H1 2027",
                  "More consistent metadata, richer semantic enrichment, and better multilingual discovery.",
                ],
                [
                  "3D optimization",
                  "Improve browser-safe 3D preparation, texture handling, performance checks, and gallery-safe output guidance.",
                  "H2 2026",
                  "Faster and more reliable 3D cultural heritage delivery across desktop and mobile.",
                ],
                [
                  "Progressive 3D delivery",
                  "Evaluate progressive delivery and multiple levels of detail where production viewer support is proven.",
                  "H1-H2 2027",
                  "Better performance for heavy 3D assets while preserving visual quality.",
                ],
                [
                  "VR inspection",
                  "Continue headset-oriented interaction hardening, controller/raycast reliability, viewer framing, and immersive metadata presentation.",
                  "H2 2026-H1 2027",
                  "More stable immersive inspection for 3D cultural heritage galleries.",
                ],
                [
                  "Offline gallery viewing",
                  "Expand offline replay around approved public assets, cache lifecycle controls, and mobile/desktop smoke testing.",
                  "H2 2026",
                  "Prepared public galleries remain inspectable when connectivity is limited.",
                ],
                [
                  "Public embeds and reuse APIs",
                  "Document public reuse surfaces and stabilize delivery expectations for published landing pages, embeds, and machine-readable records.",
                  "H2 2026-H1 2027",
                  "Partners can reuse approved public content through more stable links and embed surfaces.",
                ],
                [
                  "CHI onboarding",
                  "Create lightweight onboarding guidance, role flows, validation checklists, and example publication states for cultural heritage institutions.",
                  "H2 2026",
                  "Easier adoption by museums, galleries, archives, libraries, and heritage organizations.",
                ],
                [
                  "Observability",
                  "Continue privacy-conscious analytics, gallery-quality signals, request correlation, and operational issue visibility.",
                  "H2 2026-H1 2027",
                  "Better evidence of platform usage and delivery quality without exposing sensitive data.",
                ],
                [
                  "Spatial metadata and GIS direction",
                  "Explore location metadata, map-oriented views, and interoperability with broader cultural heritage data ecosystems.",
                  "2027",
                  "Better spatial discovery and cross-platform reuse of cultural heritage records.",
                ],
                [
                  "Product and operational maturity",
                  "Keep planning connected to Linear tickets, GitHub history, CI/CD checks, verification records, role boundaries, and infrastructure readiness.",
                  "Ongoing",
                  "Platform maturity remains auditable for technical and non-technical stakeholders.",
                ],
              ],
            },
          },
        ],
      },
      {
        heading: "Roadmap Principles",
        blocks: [
          {
            type: "list",
            items: [
              "Public features should be tied to approved publication states and public-safe records.",
              "Metadata quality should remain visible as readiness, blockers, warnings, and improvement opportunities.",
              "3D and VR capability should be presented with browser/device support boundaries.",
              "Observability should stay aggregate and privacy-conscious.",
              "Exact release dates should be treated as milestone windows unless formally approved.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "public-manual",
    label: "Public Manual",
    slug: "public-manual",
    title: "Public Manual",
    summary:
      "A lightweight guide to the public and editorial workflows behind Artetra publishing.",
    sections: [
      {
        heading: "Platform Overview",
        blocks: [
          {
            type: "paragraph",
            text:
              "Artetra helps cultural heritage teams prepare artwork records, review media and metadata, preview galleries, and publish approved public surfaces for human visitors and reuse partners.",
          },
        ],
      },
      {
        heading: "User Roles",
        blocks: [
          {
            type: "table",
            table: {
              caption: "Public-facing role model",
              headers: ["Role", "Public-facing purpose"],
              rowHeaderColumn: 0,
              rows: [
                ["Public visitor", "Views approved galleries, artworks, 3D objects, and public landing pages."],
                ["Curator or editor", "Prepares artwork records, metadata, media, source attribution, rights information, and validation corrections."],
                ["Institution administrator", "Oversees publication readiness, role access, public-gallery preparation, and approved content release."],
                ["Technical or reuse partner", "Uses public links, embeds, and machine-readable surfaces where records are eligible for reuse."],
              ],
            },
          },
        ],
      },
      {
        heading: "EDM-Compatible Workflow",
        blocks: [
          {
            type: "list",
            ordered: true,
            items: [
              "Prepare an artwork record with title, identifiers, provider/source context, rights, language values, and media references.",
              "Connect contextual resources such as people, places, time spans, or concepts where stable references are available.",
              "Review readiness and validation status before public release.",
              "Publish through Artetra public landing/embed surfaces and EDM-compatible record delivery where eligible.",
            ],
          },
          {
            type: "callout",
            title: "What EDM means",
            body:
              "EDM means Europeana Data Model. It connects cultural objects, digital representations, providers, rights, aggregations, and contextual entities for interoperable cultural heritage metadata.",
          },
        ],
      },
      {
        heading: "Controlled Vocabulary And Dereferencing Workflow",
        blocks: [
          {
            type: "list",
            ordered: true,
            items: [
              "Prefer stable authority references, thesauri, SKOS concept schemes, and dereferenceable URIs when they are available.",
              "Dereference references to check labels, language variants, relationships, and RDF metadata.",
              "Keep fetched labels and validation status visible so curators can distinguish confirmed authority data from local free-text metadata.",
              "Use unresolved or weak references as validation warnings, not as hidden failures.",
            ],
          },
        ],
      },
      {
        heading: "3D, VR, And Offline Gallery Workflow",
        blocks: [
          {
            type: "list",
            items: [
              "Review 3D assets for browser-safe format, file size, texture behavior, framing, and public-gallery suitability.",
              "Use gallery preview to inspect placement, scale, labels, audio, and movement before publication.",
              "Treat VR as an inspection surface that depends on supported devices, stable 3D assets, viewer framing, and runtime fallbacks.",
              "Prepare offline gallery viewing only for approved public assets and verify cache lifecycle behavior before relying on it.",
            ],
          },
        ],
      },
      {
        heading: "Validation Dashboard",
        blocks: [
          {
            type: "paragraph",
            text:
              "Validation is a local readiness model aligned with Europeana-style publishing-tier direction. It is not an official Europeana certification. It separates baseline blockers from warnings and higher-tier improvement opportunities.",
          },
          {
            type: "table",
            table: {
              caption: "Validation dimensions",
              headers: ["Dimension", "Checks"],
              rowHeaderColumn: 0,
              rows: [
                ["EDM structure", "Core entities, identifiers, aggregation data, web resources, public landing links, and embed delivery surfaces."],
                ["Content tiers", "Media quality, access, display suitability, rights clarity, and reuse potential."],
                ["Metadata tiers", "Language values, enabling elements, contextual classes, authority references, and dereferenceable URIs."],
                ["3D readiness", "Supported format, browser delivery, file availability, texture behavior, and viewer suitability."],
                ["Publication state", "Whether a record is draft, blocked, ready, live, planned, or an improvement opportunity."],
              ],
            },
          },
        ],
      },
      {
        heading: "Publication States",
        blocks: [
          {
            type: "table",
            table: {
              caption: "Publication states",
              headers: ["State", "Meaning"],
              rowHeaderColumn: 0,
              rows: [
                ["Draft", "The record is still being prepared."],
                ["Blocked", "Required information or media is missing, unclear, or unsupported."],
                ["Ready", "The record has passed baseline readiness checks but has not necessarily been published."],
                ["Live", "The record has an approved public surface."],
                ["Planned", "A public surface or higher-quality delivery path is planned but not yet live."],
                ["Improvement opportunity", "The record can be improved for richer discovery, reuse, or higher-quality presentation."],
              ],
            },
          },
        ],
      },
      {
        heading: "Public Reuse And Observability",
        blocks: [
          {
            type: "paragraph",
            text:
              "Public reuse focuses on stable published surfaces: artwork landing pages, embeddable viewer pages, oEmbed-style responses, and machine-readable EDM/OAI delivery where records meet readiness requirements.",
          },
          {
            type: "paragraph",
            text:
              "Observability covers privacy-safe telemetry, admin analytics, request correlation, runtime issue visibility, and operational log readiness. Public reporting should stay aggregate and redacted.",
          },
        ],
      },
    ],
  },
  {
    id: "glossary",
    label: "Glossary",
    slug: "glossary",
    title: "Glossary",
    summary:
      "Technical terms used across the platform, public roadmap, and cultural heritage publishing workflow.",
    sections: [
      {
        heading: "Terms",
        blocks: [
          {
            type: "table",
            table: {
              caption: "Artetra public glossary",
              headers: ["Term", "Meaning"],
              rowHeaderColumn: 0,
              rows: [
                ["3D asset", "A digital three-dimensional object or model used for viewing, inspection, or gallery presentation."],
                ["Aggregator", "An organization or platform that collects, validates, and forwards cultural heritage metadata and digital objects to larger discovery platforms."],
                ["Authority file", "A maintained source of authoritative identifiers and labels for people, organizations, places, concepts, or other entities."],
                ["CHI", "Cultural heritage institution, such as a museum, archive, gallery, library, or heritage organization."],
                ["Concept scheme", "A structured set of concepts, often expressed with SKOS, used to organize controlled vocabulary terms."],
                ["Controlled vocabulary", "A managed list or structured set of accepted terms used to describe things consistently across records."],
                ["Content tier", "A content-quality level used in Europeana-style publishing discussions, covering access, display, rights, and reuse conditions."],
                ["Dereferenceable URI", "A stable web identifier that can be resolved to retrieve useful information about the thing it identifies, such as labels, relationships, or RDF metadata."],
                ["EDM", "Europeana Data Model, an interoperable model for cultural heritage metadata that connects cultural objects, digital representations, provider data, rights, and contextual entities."],
                ["Linked Open Data", "Structured data published with stable identifiers and links to other data, making it easier for systems to connect and reuse metadata."],
                ["LOD (3D Level of Detail)", "Multiple versions of a 3D asset at different complexity levels, used to balance visual quality and runtime performance."],
                ["Machine-readable record", "Structured data that can be read by software systems, not only by human visitors."],
                ["Metadata tier", "A metadata-quality level used in Europeana-style publishing discussions, including language, enabling elements, and contextual classes."],
                ["OAI", "Open Archives Initiative protocol family. In this context it refers to harvesting-oriented delivery of machine-readable records."],
                ["Observability", "Telemetry, analytics, logs, request correlation, and dashboards used to understand platform behavior and detect delivery or runtime issues."],
                ["oEmbed", "A lightweight format that lets another site request embeddable display information for a public resource."],
                ["Offline gallery viewing", "A workflow where approved public gallery assets are prepared so a gallery can be replayed when connectivity is limited."],
                ["RDF", "Resource Description Framework, a linked-data format used to express structured metadata as machine-readable statements."],
                ["SKOS", "Simple Knowledge Organization System, a web standard commonly used to publish controlled vocabularies, thesauri, and concept schemes as linked data."],
                ["Thesaurus", "A structured controlled vocabulary that can include preferred labels, alternate labels, broader/narrower terms, and related concepts."],
                ["URI", "Uniform Resource Identifier, a stable identifier used to refer to a resource, concept, person, place, or other entity."],
                ["Validation dashboard", "A review surface that shows blockers, warnings, readiness dimensions, media support, metadata gaps, and publication status."],
                ["VR", "Virtual reality, an immersive viewing mode for inspecting 3D galleries and cultural heritage assets through headset-oriented interaction."],
              ],
            },
          },
        ],
      },
    ],
  },
  {
    id: "spatial",
    label: "Moving from Spatial.io",
    slug: "spatial",
    title: "Moving from Spatial.io? Keep your 3D world online with Artetra.",
    summary:
      "A public checklist for creators and institutions reviewing migration options after Spatial's Creator platform sunset notice.",
    sections: [
      {
        heading: "What Happened",
        blocks: [
          {
            type: "paragraph",
            text:
              "Spatial announced that its Free and Pro Creator platform tiers and Creator 3D World hosting will sunset on 27 July 2026. Spatial Enterprise remains separate. Artetra is not presented as a Spatial partner; this guidance is for users who need a browser-based cultural or creative 3D publishing path.",
          },
          {
            type: "callout",
            title: "Source",
            body: "Spatial public notice: https://www.spatial.io/blog/spatial-creator-platform-sunsetting",
          },
        ],
      },
      {
        heading: "What Users Should Do Before 27 July 2026",
        blocks: [
          {
            type: "list",
            items: [
              "Export or download available assets from Spatial before access and hosting end.",
              "Preserve world links, screenshots, descriptions, credits, rights notes, audio references, and interaction notes.",
              "Record which objects, textures, videos, sounds, or documents belong to each world.",
              "Identify the most important worlds to preserve first.",
              "Check whether the exported assets are suitable for browser-based delivery or need optimization.",
            ],
          },
        ],
      },
      {
        heading: "How Artetra Can Help",
        blocks: [
          {
            type: "paragraph",
            text:
              "Artetra can help cultural, educational, gallery, brand, and creator teams review exported assets, plan a browser-ready 3D gallery path, prepare contextual metadata, and publish an initial public Artetra experience where the assets and rights context are suitable.",
          },
          {
            type: "table",
            table: {
              caption: "Spatial migration support areas",
              headers: ["Need", "Artetra direction"],
              rowHeaderColumn: 0,
              rows: [
                ["World preservation", "Review exported world assets and identify what can become a public Artetra gallery experience."],
                ["3D readiness", "Check model format, file size, texture behavior, viewer framing, and browser suitability."],
                ["Context", "Prepare titles, descriptions, creator/source information, rights notes, and reuse context."],
                ["Publishing", "Support an initial Artetra experience with public-gallery review and publication guidance."],
              ],
            },
          },
        ],
      },
      {
        heading: "Free First Migration Review",
        blocks: [
          {
            type: "list",
            items: [
              "Asset review.",
              "Migration feasibility check.",
              "First consultation.",
              "Import guidance.",
              "Environment setup support.",
              "Browser-readiness review.",
              "Publication support for one initial Artetra experience.",
            ],
          },
        ],
      },
      {
        heading: "Details To Prepare",
        blocks: [
          {
            type: "paragraph",
            text:
              "Share these details so the Artetra team can review your Spatial migration request and contact you about next steps.",
          },
          {
            type: "form",
            form: {
              caption: "Spatial migration details checklist",
              submitLabel: "Submit migration review request",
              fields: [
                {
                  id: "name",
                  label: "Name",
                  hint: "Identifies the person coordinating the review.",
                  placeholder: "Jane Doe",
                },
                {
                  id: "email",
                  label: "Email",
                  hint: "Gives the team a contact path outside this display-only page.",
                  inputType: "email",
                  placeholder: "name@example.com",
                },
                {
                  id: "organisation",
                  label: "Organisation",
                  hint: "Clarifies whether the migration is individual, institutional, educational, gallery, brand, or public-sector.",
                  placeholder: "Museum, gallery, university, or brand",
                },
                {
                  id: "spatialWorldLink",
                  label: "Spatial world link",
                  hint: "Shows which world or experience needs review while the link remains available.",
                  inputType: "url",
                  placeholder: "https://spatial.io/s/...",
                },
                {
                  id: "typeOfExperience",
                  label: "Type of experience",
                  hint: "Distinguishes gallery, classroom, event, brand space, museum exhibit, or other use cases.",
                  placeholder: "Gallery, classroom, event, museum exhibit...",
                },
                {
                  id: "numberOfWorlds",
                  label: "Number of worlds",
                  hint: "Helps estimate migration scope.",
                  inputType: "number",
                  placeholder: "1",
                },
                {
                  id: "assetExportStatus",
                  label: "Asset export status",
                  hint: "Shows whether files are already backed up or still need export.",
                  placeholder: "Exported, in progress, or not started",
                },
                {
                  id: "targetDeadline",
                  label: "Target deadline",
                  hint: "Prioritizes work before 27 July 2026.",
                  inputType: "date",
                },
                {
                  id: "creatorOrInstitutionCategory",
                  label: "Creator or institution category",
                  hint: "Individual creator, museum, university, gallery, brand, or public institution.",
                  placeholder: "Individual creator, museum, university, gallery...",
                },
              ],
            },
          },
        ],
      },
    ],
  },
];

export const defaultPlatformTab = platformTabs[0];

export const getPlatformTabBySlug = (slug: string | null | undefined): PlatformTab =>
  platformTabs.find((tab) => tab.slug === slug) || defaultPlatformTab;
